import React from "react";
import { Link } from "react-router-dom";
import { Handshake, Rocket, ShieldCheck, ArrowRight, TrendingUp, Users, Building2, Zap } from "lucide-react";
import useSEO from "@/lib/useSEO";
import EnquiryForm from "@/components/EnquiryForm";

const BENEFITS = [
  { icon: Users, title: "Access verified talent", body: "1,500+ skill-tagged frontline workers ready for deployment — welders, fitters, riggers, electricians, technicians." },
  { icon: Rocket, title: "72-hour placement SLA", body: "From RFQ to boots-on-site — shutdown crews mobilised faster than any traditional agency." },
  { icon: ShieldCheck, title: "Compliance & safety", body: "PF/ESIC, safety briefing, site induction — all pre-handled before workers reach your gate." },
  { icon: TrendingUp, title: "Co-marketing upside", body: "Case studies, co-branded content, and joint webinars amplify your reach into the industrial employer segment." },
];

const PARTNER_TYPES = [
  { icon: Building2, name: "HR-tech platforms", tag: "Awign · TaskMo · BetterPlace · Vahan · GigIndia" },
  { icon: Zap, name: "Trade & industry bodies", tag: "APP · IEEMA · CREDAI regional chapters" },
  { icon: Users, name: "Skilling partners", tag: "NAPS · NSDC · Skill India registered orgs" },
  { icon: Handshake, name: "EPC contractors", tag: "L&T · Thermax · BHEL vendor ecosystem" },
];

export default function Partners() {
  useSEO({
    title: "Partner With Sarthi Associates — Frontline Workforce for HR-Tech, EPC & Skilling Orgs",
    description: "Partner with Sarthi Associates to plug India's biggest gap in frontline workforce supply. 1,500+ verified workers, 20+ EPC contractor partners, deploying across power plants and refineries.",
    path: "/partners",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "Partner with Sarthi Associates",
      "url": "https://sarthiassociates.com/partners",
      "isPartOf": { "@type": "WebSite", "name": "Sarthi Associates", "url": "https://sarthiassociates.com" },
    },
  });

  return (
    <div>
      <section className="border-b-2 border-sarthi-border bg-white">
        <div className="mx-auto max-w-7xl px-4 py-14 sm:px-6 lg:py-20">
          <div className="inline-flex items-center gap-2 rounded-full bg-sarthi-accent px-4 py-2 text-sm font-bold uppercase tracking-wider text-sarthi-primary">
            <Handshake className="h-4 w-4" /> Partnership Program
          </div>
          <h1 className="mt-4 max-w-4xl font-display text-4xl font-black leading-[1.05] text-sarthi-text sm:text-5xl lg:text-6xl">
            Grow your platform with{" "}
            <span className="text-sarthi-primary">India's most trusted</span> frontline workforce.
          </h1>
          <p className="mt-5 max-w-3xl text-lg font-medium text-sarthi-muted sm:text-xl">
            Sarthi Associates is opening a limited partnership program for HR-tech
            platforms, trade associations, skilling orgs, and EPC contractors who want
            to unlock the industrial-workforce vertical without building it themselves.
          </p>
          <div className="mt-8 flex flex-col gap-3 sm:flex-row">
            <a href="#partner-form" data-testid="partners-cta-form" className="sarthi-btn-primary inline-flex items-center justify-center gap-2 px-6 py-4 text-lg text-white">
              Become a partner <ArrowRight className="h-5 w-5" />
            </a>
            <Link to="/about" className="sarthi-btn-outline inline-flex items-center justify-center gap-2 px-6 py-4 text-lg">
              Read our story
            </Link>
          </div>
        </div>
      </section>

      <section className="border-b-2 border-sarthi-border">
        <div className="mx-auto max-w-7xl px-4 py-14 sm:px-6">
          <div className="mb-8">
            <div className="text-sm font-bold uppercase tracking-wider text-sarthi-primary">Mutual wins</div>
            <h2 className="mt-1 font-display text-3xl font-extrabold text-sarthi-text sm:text-4xl">
              Why partners choose Sarthi
            </h2>
          </div>
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {BENEFITS.map((b) => (
              <div key={b.title} data-testid={`benefit-${b.title.split(" ")[0].toLowerCase()}`} className="sarthi-card p-6">
                <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-sarthi-accent text-sarthi-primary">
                  <b.icon className="h-6 w-6" strokeWidth={2.5} />
                </div>
                <div className="font-display text-xl font-extrabold text-sarthi-text">{b.title}</div>
                <p className="mt-2 text-base font-medium text-sarthi-muted">{b.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="border-b-2 border-sarthi-border bg-sarthi-accent/40">
        <div className="mx-auto max-w-7xl px-4 py-14 sm:px-6">
          <div className="mb-8">
            <div className="text-sm font-bold uppercase tracking-wider text-sarthi-primary">Who we work with</div>
            <h2 className="mt-1 font-display text-3xl font-extrabold text-sarthi-text sm:text-4xl">
              Ideal partner profiles
            </h2>
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {PARTNER_TYPES.map((p) => (
              <div key={p.name} className="sarthi-card flex items-start gap-4 p-5">
                <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl bg-sarthi-primary text-white">
                  <p.icon className="h-6 w-6" strokeWidth={2.5} />
                </div>
                <div>
                  <div className="font-display text-lg font-extrabold text-sarthi-text">{p.name}</div>
                  <div className="text-sm font-semibold text-sarthi-muted">{p.tag}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="partner-form" className="scroll-mt-24 bg-white">
        <div className="mx-auto max-w-7xl px-4 py-14 sm:px-6">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-5 lg:gap-14">
            <div className="lg:col-span-2">
              <div className="text-sm font-bold uppercase tracking-wider text-sarthi-primary">Talk to us</div>
              <h2 className="mt-2 font-display text-3xl font-extrabold text-sarthi-text sm:text-4xl">
                Start the conversation
              </h2>
              <p className="mt-3 text-base font-medium leading-relaxed text-sarthi-muted">
                Fill in the form — Sanjay (MD) or one of our Executive Directors
                will reach out inside 24 hours to explore fit and next steps.
                No boilerplate deck, no gatekeepers.
              </p>
              <div className="mt-6 rounded-2xl bg-sarthi-accent/70 p-5">
                <div className="text-sm font-bold uppercase tracking-wide text-sarthi-primary">Direct line</div>
                <div className="mt-1 font-display text-2xl font-extrabold text-sarthi-text">+91 91318 47732</div>
                <div className="text-sm font-medium text-sarthi-muted">contact@sarthiassociates.com · Noida, India</div>
              </div>
            </div>
            <div className="lg:col-span-3">
              <EnquiryForm />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
